import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../app/store'
import { useProfile } from '../hooks/useProfile'
import LoadingSpinner from '../components/LoadingSpinner'
import TopBar from '../components/home/TopBar'
import IconBack from '../components/profile/IconBack'
import ProfileDateButton from '../components/profile/ProfileDateButton'
import KPICard from '../components/profile/KPICard'
import SalesClassificationTable from '../components/profile/SalesClassificationTable'
import MostSoldItemsTable from '../components/profile/MostSoldItemsTable'
import ShiftReportsTable from '../components/profile/ShiftReportsTable'
import moneyIcon from '../assets/images/money.svg'
import orderIcon from '../assets/images/order.svg'
import coIcon from '../assets/images/co.svg'
import sarImage from '../assets/images/SAR.svg'

const ProfilePage: React.FC = () => {
  const { t } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  const isRTL = settings.language === 'ar'

  const {
    profileData,
    isLoading,
    error,
    isCS,
    selectedDateRange,
    applyDateFilter,
    clearDateFilter,
    formatNumber,
    formatShiftDate,
    employeeName,
    totalSales,
    totalOrders,
    totalProducts,
    cashSales,
    cashOrdersCount,
    networkSales,
    networkOrdersCount,
    partialSales,
    partialOrdersCount,
    bestSellingProducts,
    shifts,
    getRecentShifts,
    refreshProfile,
  } = useProfile()

  // Responsive breakpoints (matching Flutter)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isWide = windowWidth >= 1200
  const isTablet = windowWidth >= 800 && windowWidth < 1200
  const kpiCols = isWide ? 3 : isTablet ? 3 : 1
  const sidePanelWidth = isWide ? 500 : isTablet ? 400 : '100%'

  // Handle refresh (pull to refresh)
  const handleRefresh = async () => {
    await refreshProfile()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <p className="text-red-600 text-center font-['IBM_Plex_Sans_Arabic']">
              {t('error_loading_profile')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <p className="text-gray-600 text-center font-['IBM_Plex_Sans_Arabic']">
              {t('no_data')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
        {/* AppBar */}
        <div className={` pt-2 ${isRTL ? 'pr-0 pl-4' : 'pl-0 pr-4'}`}>
          <TopBar userName={employeeName} />
        </div>
        <div
      className="w-full h-screen bg-[#F5F5F5] p-4  overflow-y-auto"
      dir={isRTL ? 'rtl' : 'ltr'}
      onScroll={(e) => {
        // Handle pull to refresh if needed
        const target = e.target as HTMLElement
        if (target.scrollTop === 0) {
          // Could implement pull to refresh here
        }
      }}
    >
      
      
      <div className="max-w-full mx-auto">
      

        {/* Back button + Employee name + Date button row */}
        <div className="px-2   mb-1">
          <div className="h-10 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <IconBack />
              <span className="text-sm font-medium font-['IBM_Plex_Sans_Arabic']">
                {employeeName}
              </span>
            </div>
            <ProfileDateButton
              selectedDateRange={selectedDateRange}
              onApply={applyDateFilter}
              onClear={clearDateFilter}
              height={30}
              size={15}
            />
          </div>
        </div>

        {/* KPI Cards Grid (Responsive) */}
        <div
          className="grid gap-0 mb-1"
          style={{
            gridTemplateColumns: `repeat(${kpiCols}, 1fr)`,
          }}
        >
          <KPICard
            title={t('total_sales')}
            iconSvg={moneyIcon}
            number={formatNumber(totalSales)}
            text=""
            textIcon={sarImage}
            height={80}
            left={5}
            right={5}
          />
          <KPICard
            title={t('total_orders')}
            iconSvg={orderIcon}
            number={formatNumber(totalOrders)}
            text={t('order_text')}
            textIcon=""
            height={80}
            left={5}
            right={5}
          />
          <KPICard
            title={t('total_items')}
            iconSvg={coIcon}
            number={formatNumber(totalProducts)}
            text={t('item_text')}
            textIcon=""
            height={80}
            left={5}
            right={5}
          />
        </div>

        {/* Main Content - Responsive Layout */}
        {isWide || isTablet ? (
          // Desktop/Tablet: Side-by-side layout
          <div className="flex gap-2 items-start">
            {/* Left Column: Sales Classification + Best Selling */}
            <div
              className="flex flex-col gap-2"
              style={{ width: sidePanelWidth }}
            >
              <SalesClassificationTable
                profile={profileData}
                isCS={isCS}
                formatNumber={formatNumber}
              />
              <div className="h-2" /> {/* SizedBox(height: 10) */}
              <MostSoldItemsTable
                products={bestSellingProducts}
                formatNumber={formatNumber}
                fullWidth={false}
                isLoading={isLoading}
              />
            </div>

            {/* Right Column: Shift Reports (only if !isCS) */}
            {!isCS && (
              <>
                <div className="w-2 " /> {/* SizedBox(width: 10) */}
                <div className="flex-1">
                  <ShiftReportsTable
                    shifts={shifts}
                    formatNumber={formatNumber}
                    formatDate={formatShiftDate}
                    isLoading={isLoading}
                    getRecentShifts={getRecentShifts}
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          // Mobile: Stacked layout
          <div className="flex flex-col gap-2">
            {/* Shift Reports first (only if !isCS) */}
            {!isCS && (
              <>
                <ShiftReportsTable
                  shifts={shifts}
                  formatNumber={formatNumber}
                  formatDate={formatShiftDate}
                  isLoading={isLoading}
                  getRecentShifts={getRecentShifts}
                />
                <div className="h-2 " /> {/* SizedBox(height: 10) */}
              </>
            )}
            {/* Sales Classification */}
            <SalesClassificationTable
              profile={profileData}
              isCS={isCS}
              formatNumber={formatNumber}
            />
            <div className="h-2.5" /> {/* SizedBox(height: 10) */}
            {/* Best Selling */}
            <MostSoldItemsTable
              products={bestSellingProducts}
              formatNumber={formatNumber}
              fullWidth={false}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
      <div className="w-full h-[120px]  "></div>
    </div>
    </div>
    
  )
}

export default ProfilePage
